import axios, { AxiosInstance } from "axios";
import { toast } from "react-hot-toast";
import config from "@/config";
import { API_BASE_URL } from "@/utils/api";
import {
  DeviceControl,
  GroupControlResponse,
  Device,
  DeviceSettings,
} from "@/services/types";
import type {
  UnifiedArea,
  UnifiedLightDevice,
  UnifiedSensor,
  UnifiedShade,
  UnifiedScene,
  UnifiedLightGroup,
  UnifiedSecurityEvent,
} from "@/types/unified";
import { auth } from "./firebase";
import { signOut as firebaseSignOut } from "firebase/auth";
import { handleResponse } from "./api/utils";
import Cookies from "js-cookie";

// Custom error class for API errors
export class APIError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string,
    public details?: any,
  ) {
    super(message);
    this.name = "APIError";
  }
}

// Dynamic API URL based on client's window location
// Imported from @/utils/api

// Remove trailing slash if present
const normalizeUrl = (url: string): string => {
  return url.endsWith("/") ? url.slice(0, -1) : url;
};

// Get full API URL
const getApiUrl = (endpoint: string): string => {
  const baseUrl = normalizeUrl(API_BASE_URL);
  const path = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  return `${baseUrl}${path}`;
};

// Type: AxiosInstance plus our custom fetchJson (so get/post/put/delete are recognized)
export type ApiClient = AxiosInstance & {
  fetchJson<T>(url: string, options?: RequestInit): Promise<T>;
};

// Create axios instance with interceptors
export const api: ApiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
}) as ApiClient;

// Add retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Function to delay execution
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Function to manually refresh token (for debugging)
export const forceRefreshToken = async () => {
  console.log("Force refreshing token...");
  const newToken = await refreshApiToken();
  console.log("New token received:", newToken ? "Yes" : "No");
  return newToken;
};

// Function to get Firebase token and exchange it for API token
const refreshApiToken = async (): Promise<string | null> => {
  try {
    // Never use mock token in production
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.log("No current user found");
      return null;
    }

    console.log("Current user found:", currentUser.email);

    // Get Firebase ID token directly
    const firebaseToken = await currentUser.getIdToken(true);
    console.log("Got Firebase token:", firebaseToken ? "Token received" : "No token");

    // Store the Firebase token directly (no exchange needed)
    localStorage.setItem("api_jwt", firebaseToken);
    return firebaseToken;
  } catch (error: any) {
    console.error("Failed to refresh API token:", error);
    return null;
  }
};

// Attach API token from localStorage to every request
api.interceptors.request.use((config) => {
  // const apiJwt = localStorage.getItem("api_jwt");
  if (typeof window !== "undefined") {

    const apiJwt = localStorage.getItem("refreshToken");
    if (apiJwt) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${apiJwt}`;
    }
  }
  return config;
});

// Area endpoints
export const areaApi = {
  async getAll() {
    const response = await api.get("/api/spaces");
    return { success: response.data.success, areas: response.data.spaces || [] };
  },

  /** Get all spaces as unified Area DTOs (?format=unified). */
  async getAllUnified(): Promise<{ success: boolean; areas: UnifiedArea[] }> {
    const response = await api.get("/api/spaces", { params: { format: "unified" } });
    return { success: response.data.success, areas: response.data.areas || [] };
  },

  async getById(id: string) {
    const response = await api.get(`/api/spaces/${id}`);
    return { success: response.data.success, area: response.data.space || null };
  },

  /** Get space by id as unified Area DTO (?format=unified). */
  async getByIdUnified(id: string): Promise<{ success: boolean; area: UnifiedArea | null }> {
    const response = await api.get(`/api/spaces/${id}`, { params: { format: "unified" } });
    return { success: response.data.success, area: response.data.area ?? null };
  },

  async create(area: any) {
    const response = await api.post("/api/spaces", area);
    return { success: response.data.success, area: response.data.space };
  },

  async update(id: string, area: any) {
    const response = await api.put(`/api/spaces/${id}`, area);
    return { success: response.data.success, area: response.data.space };
  },

  async delete(id: string) {
    await api.delete(`/api/spaces/${id}`);
    return { success: true };
  },

  async getDevices(areaId: string) {
    const response = await api.get(`/api/spaces/${areaId}/devices`);
    return { success: true, devices: response.data || [] };
  },

  async updateStatus(id: string, status: "active" | "inactive") {
    const response = await api.patch(`/api/spaces/${id}/status`, { status });
    return { success: true, area: response.data };
  },

  async ensureDefaults() {
    const response = await api.post('/api/spaces/ensure-defaults');
    return { success: response.data.success };
  },
};

// Add simple fetch method to the API for cases where axios isn't suitable
api.fetchJson = async <T>(url: string, options?: RequestInit): Promise<T> => {
  const response = await fetch(getApiUrl(url), options);
  return handleResponse<T>(response);
};

// Function to handle auto logout when authentication fails
const handleAutoLogout = async () => {
  try {
    // Clear all tokens from localStorage
    if (typeof window !== "undefined") {
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("api_jwt");
    }

    // Clear token cookie
    Cookies.remove("token");

    // Sign out from Firebase
    if (auth.currentUser) {
      await firebaseSignOut(auth);
    }

    // Show logout message
    toast.error("Session expired. Please login again.");

    // Only redirect if not already on login/forgot-password to avoid reload loop
    // (e.g. after docker down -v, backend is fresh but browser still has old token;
    // 401s then cause repeated redirects to /login and page keeps reloading)
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const isAuthPage = path === "/login" || path === "/forgot-password" || path.startsWith("/login");
    if (!isAuthPage) {
      window.location.href = "/login";
    }
  } catch (logoutError) {
    console.error("Error during auto logout:", logoutError);
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const isAuthPage = path === "/login" || path === "/forgot-password" || path.startsWith("/login");
    if (!isAuthPage) {
      window.location.href = "/login";
    }
  }
};

// Messages that mean auth is invalid → logout immediately (no retry)
const AUTH_ERROR_LOGOUT_PHRASES = [
  "authorization header missing",
  "authentication error",
  "invalid authorization format",
  "invalid authorization",
  "missing authorization",
];

const shouldLogoutDueToAuthError = (err: any): boolean => {
  const msg = (
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    ""
  ).toLowerCase();
  return AUTH_ERROR_LOGOUT_PHRASES.some((phrase) => msg.includes(phrase));
};

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Any auth-related message → logout immediately
    if (shouldLogoutDueToAuthError(error)) {
      await handleAutoLogout();
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh the token
        const newToken = await refreshApiToken();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error("Failed to refresh token:", refreshError);
      }

      // If token refresh fails, auto logout
      await handleAutoLogout();
      return Promise.reject(error);
    }

    // Handle 403 Forbidden errors (authentication failed)
    if (error.response?.status === 403) {
      // Check if it's an authentication-related 403
      const errorMessage = error.response?.data?.error || error.response?.data?.message || "";
      if (
        errorMessage.toLowerCase().includes("unauthorized") ||
        errorMessage.toLowerCase().includes("forbidden") ||
        errorMessage.toLowerCase().includes("token") ||
        errorMessage.toLowerCase().includes("authentication") ||
        errorMessage.toLowerCase().includes("inactive")
      ) {
        await handleAutoLogout();
        return Promise.reject(error);
      }
    }

    // Handle 404 errors
    if (error.response?.status === 404) {
      toast.error("Resource not found");
    }

    // Handle other errors
    if (error.response?.status >= 500) {
      toast.error("Server error occurred");
    }

    return Promise.reject(error);
  },
);

// Unified device DTO = LightDevice | Sensor | Shade (discriminate by presence of shadeLevel, lastReading, etc.)
export type UnifiedDeviceDto = UnifiedLightDevice | UnifiedSensor | UnifiedShade;

// Device endpoints
export const deviceApi = {
  async getAll() {
    const response = await api.get("/api/devices");
    return { success: true, devices: response.data };
  },

  /** Get all devices as unified DTOs (?format=unified). */
  async getAllUnified(): Promise<{ success: boolean; devices: UnifiedDeviceDto[] }> {
    const response = await api.get("/api/devices", { params: { format: "unified" } });
    const devices = response.data?.devices ?? [];
    return { success: response.data?.success ?? true, devices };
  },

  /** Get live devices as unified DTOs (?format=unified). */
  async getLiveUnified(): Promise<{ success: boolean; devices: UnifiedDeviceDto[] }> {
    const response = await api.get("/api/devices/live", { params: { format: "unified" } });
    const devices = response.data?.devices ?? [];
    return { success: response.data?.success ?? true, devices };
  },

  async getByCategory(category: string) {
    const response = await api.get(`/api/devices?category=${encodeURIComponent(category)}`);
    return { success: true, devices: response.data };
  },

  async getBySubtype(subType: string) {
    const response = await api.get(`/api/devices?subType=${encodeURIComponent(subType)}`);
    return { success: true, devices: response.data };
  },

  async getById(id: string) {
    const response = await api.get(`/api/devices/${id}`);
    return { success: true, device: response.data };
  },

  /** Get device by id as unified DTO (?format=unified). */
  async getByIdUnified(id: string): Promise<{ success: boolean; device: UnifiedDeviceDto | null }> {
    const response = await api.get(`/api/devices/${id}`, { params: { format: "unified" } });
    return { success: response.data?.success ?? true, device: response.data?.device ?? null };
  },

  async updateState(id: string, state: Partial<DeviceSettings>) {
    const response = await api.post(`/api/devices/${id}/state`, { state });
    return { success: true, message: response.data.message || "State updated successfully" };
  },

  async updateDeviceState(id: string, state: Partial<DeviceSettings>) {
    const response = await api.put(`/api/device-state/${id}`, { state });
    return { success: true, message: response.data.message || "Device state updated successfully" };
  },

  async bulkUpdate(
    updates: Array<{ id: string; state: Partial<DeviceSettings> }>,
  ) {
    const response = await api.post("/api/device-state/bulk", { updates });
    return { success: true, message: response.data.message || "Bulk update completed successfully" };
  },

  delete: async (id: string) => {
    const response = await api.delete(`/api/devices/${id}`);
    return { success: true, message: response.data.message || "Device deleted successfully" };
  },

  groupControl: async (
    controls: DeviceControl[],
  ): Promise<GroupControlResponse> => {
    const response = await api.put<GroupControlResponse>("/api/devices/group", {
      controls,
    });

    // Handle partial failures
    if (response.data.operations.failed > 0) {
      // Create detailed error message
      const failureDetails = response.data.failures;
      const errorMessage = `Failed to update ${response.data.operations.failed} out of ${response.data.operations.total} devices`;

      // Throw custom error with detailed information
      throw new APIError(errorMessage, response.status, "PARTIAL_FAILURE", {
        operations: response.data.operations,
        failures: failureDetails,
        results: response.data.results,
      });
    }

    return response.data;
  },
};

// Gateway endpoints
export const gatewayApi = {
  getAll: () => {
    return api.get("/api/gateways");
  },
  getById: (id: string) => {
    return api.get(`/api/gateways/${id}`);
  },
  create: (gateway: any) => {
    return api.post("/api/gateways", gateway);
  },
  update: (id: string, gateway: any) => {
    return api.put(`/api/gateways/${id}`, gateway);
  },
  delete: (id: string) => {
    return api.delete(`/api/gateways/${id}`);
  },
};

// Unified DTO API - all return canonical shapes with optional vendorMeta
export const unifiedApi = {
  getAreas: (): Promise<{ success: boolean; areas: UnifiedArea[] }> =>
    api.get("/api/spaces", { params: { format: "unified" } }).then((r) => ({ success: r.data.success, areas: r.data.areas ?? [] })),

  getArea: (id: string): Promise<{ success: boolean; area: UnifiedArea | null }> =>
    api.get(`/api/spaces/${id}`, { params: { format: "unified" } }).then((r) => ({ success: r.data.success, area: r.data.area ?? null })),

  getScenes: (): Promise<{ success: boolean; scenes: UnifiedScene[] }> =>
    api.get("/api/scenes", { params: { format: "unified" } }).then((r) => ({ success: r.data.success, scenes: r.data.scenes ?? [] })),

  getScene: (id: string): Promise<{ success: boolean; scene: UnifiedScene | null }> =>
    api.get(`/api/scenes/${id}`, { params: { format: "unified" } }).then((r) => ({ success: r.data.success, scene: r.data.scene ?? null })),

  getGroups: (): Promise<{ success: boolean; groups: UnifiedLightGroup[] }> =>
    api.get("/api/groups", { params: { format: "unified" } }).then((r) => ({ success: r.data.success, groups: r.data.groups ?? [] })),

  getGroup: (id: string): Promise<{ success: boolean; group: UnifiedLightGroup | null }> =>
    api.get(`/api/groups/${id}`, { params: { format: "unified" } }).then((r) => ({ success: r.data.success, group: r.data.group ?? null })),

  getSecurityEvents: (): Promise<{ success: boolean; securityEvents: UnifiedSecurityEvent[] }> =>
    api.get("/api/alerts", { params: { format: "unified" } }).then((r) => ({ success: r.data.success, securityEvents: r.data.securityEvents ?? [] })),

  getSecurityEvent: (id: string): Promise<{ success: boolean; securityEvent: UnifiedSecurityEvent | null }> =>
    api.get(`/api/alerts/${id}`, { params: { format: "unified" } }).then((r) => ({ success: r.data.success, securityEvent: r.data.securityEvent ?? null })),
};